// src/components/compliance/modals/DismissedRecommendationsModal.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React, { useCallback, useEffect, useState } from "react";
import ModalShell from "./shared/ModalShell";
import ModalSection from "./shared/ModalSection";
import ModalActionRow from "./shared/ModalActionRow";

const CONNECTOR_PROVIDER_DISPLAY_NAMES = {
  aws: "AWS",
  cloudflare: "Cloudflare",
  github: "GitHub",
  google_workspace: "Google Workspace",
  okta: "Okta",
};

function cleanConnectorProvider(value) {
  return String(value || "").trim().toLowerCase();
}

function formatConnectorProvider(value) {
  const normalizedProvider = cleanConnectorProvider(value);

  if (CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider]) {
    return CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider];
  }

  return String(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function titleCaseLabel(value, fallback = "—") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatRecommendationPriority(value) {
  const normalized = String(value || "medium").trim().toLowerCase();
  if (normalized === "critical") return "Critical";
  if (normalized === "high") return "High";
  if (normalized === "low") return "Low";
  return "Medium";
}

function priorityColor(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "critical" || normalized === "high") return "#fb7185";
  if (normalized === "medium") return "#fbbf24";
  return "#34d399";
}

function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter((item) => item !== undefined && item !== null && item !== "");
  }
  return [value].filter((item) => item !== undefined && item !== null && item !== "");
}

function formatList(value, fallback = "—") {
  const items = normalizeList(value)
    .map((item) => String(item).trim())
    .filter(Boolean);

  return items.length ? items.join(", ") : fallback;
}

function formatRefreshError(error) {
  const text = String(error || "").trim();
  if (!text) return "";

  if (text.toLowerCase() === "failed to fetch") {
    return "Dismissed recommendations could not be refreshed. Check the API connection and try again.";
  }

  return text;
}

const summaryBarStyle = {
  border: "1px solid rgba(96, 165, 250, 0.22)",
  borderRadius: 16,
  padding: "12px 14px",
  background: "rgba(37, 99, 235, 0.10)",
  color: "#bfdbfe",
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "center",
  flexWrap: "wrap",
};

const summaryCountStyle = {
  color: "#f8fafc",
  fontSize: 14,
  fontWeight: 900,
};

const summaryHintStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.45,
};

const listStyle = {
  display: "grid",
  gap: 12,
};

const itemCardStyle = {
  border: "1px solid rgba(96, 165, 250, 0.26)",
  borderRadius: 16,
  padding: 16,
  background: "rgba(15, 23, 42, 0.42)",
  display: "grid",
  gap: 12,
};

const chipRowStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
};

const chipStyle = {
  borderRadius: 999,
  padding: "4px 9px",
  fontSize: 11,
  fontWeight: 900,
  background: "rgba(96, 165, 250, 0.14)",
  border: "1px solid rgba(96, 165, 250, 0.22)",
  color: "#bfdbfe",
};

const detailGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
  color: "#bfdbfe",
  fontSize: 13,
};

const bodyTextStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.55,
  margin: 0,
};

const noticeStyle = {
  border: "1px solid rgba(96, 165, 250, 0.35)",
  borderRadius: 14,
  padding: "10px 12px",
  background: "rgba(37, 99, 235, 0.12)",
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.45,
};

const errorStyle = {
  ...noticeStyle,
  border: "1px solid rgba(248, 113, 113, 0.38)",
  background: "rgba(127, 29, 29, 0.20)",
  color: "#fecaca",
};

export default function DismissedRecommendationsModal({
  open,
  items = [],
  count = 0,
  loading = false,
  error = "",
  message = "",
  restoringId = null,
  onClose,
  onRefresh,
  onRestore,
}) {
  const dismissedItems = Array.isArray(items) ? items : [];
  const hiddenCount = Number.isFinite(Number(count)) ? Number(count) : dismissedItems.length;
  const formattedError = formatRefreshError(error);
  const [refreshPulse, setRefreshPulse] = useState(false);
  const isRefreshing = Boolean(loading || refreshPulse);

  useEffect(() => {
    if (!open) {
      setRefreshPulse(false);
    }
  }, [open]);

  const handleRefresh = useCallback(() => {
    if (isRefreshing) return;

    setRefreshPulse(true);
    onRefresh?.();

    window.setTimeout(() => {
      setRefreshPulse(false);
    }, 1200);
  }, [isRefreshing, onRefresh]);

  return (
    <ModalShell
      open={open}
      ariaLabel="Dismissed recommendations"
      eyebrow="OASYS · Recommendation Recall"
      title="Dismissed Recommendations"
      subtitle="Review recommendations hidden by operator feedback and restore them when they should return to active guidance."
      onClose={onClose}
      maxWidth="900px"
      accentBorder="rgba(96, 165, 250, 0.52)"
      zIndex={165}
    >
      <div style={summaryBarStyle}>
        <div>
          <div style={summaryCountStyle}>
            {hiddenCount} dismissed recommendation{hiddenCount === 1 ? "" : "s"} hidden for this tenant
          </div>
          <div style={summaryHintStyle}>
            {isRefreshing
              ? "Refreshing dismissed recommendation recall…"
              : "Restore returns a recommendation to active guidance without executing any work."}
          </div>
        </div>

        <button
          type="button"
          className="btn btn-ghost btn-xs"
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{ whiteSpace: "nowrap" }}
        >
          {isRefreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {message ? <div style={noticeStyle}>{message}</div> : null}
      {formattedError && !isRefreshing ? <div style={errorStyle}>{formattedError}</div> : null}

      <ModalSection
        title="Hidden recommendations"
        description="Restore only changes recommendation visibility/status. It does not remediate, close tasks, approve evidence, advance workflows, or run validation."
        badge={isRefreshing ? "Refreshing" : `${dismissedItems.length} hidden`}
        badgeTone={isRefreshing ? "medium" : "default"}
      >
        {isRefreshing ? (
          <p style={bodyTextStyle}>Refreshing dismissed recommendations…</p>
        ) : dismissedItems.length === 0 ? (
          <p style={bodyTextStyle}>No dismissed recommendations are currently hidden.</p>
        ) : (
          <div style={listStyle}>
            {dismissedItems.map((item) => {
              const isRestoring = String(restoringId || "") === String(item.id || "");
              const priority = formatRecommendationPriority(item.priority);

              return (
                <article key={item.id || item.recommendation_key || item.title} style={itemCardStyle}>
                  <div style={chipRowStyle}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <span
                        style={{
                          ...chipStyle,
                          color: priorityColor(item.priority),
                          borderColor: priorityColor(item.priority),
                          background: "rgba(15, 23, 42, 0.36)",
                        }}
                      >
                        {priority}
                      </span>
                      <span style={chipStyle}>{titleCaseLabel(item.category || item.recommendation_type)}</span>
                      <span style={chipStyle}>
                        {item.affected_connector ? formatConnectorProvider(item.affected_connector) : "No connector"}
                      </span>
                    </div>
                    <strong style={{ color: "#bfdbfe", fontSize: 12 }}>
                      Dismissed {formatDateTime(item.dismissed_at || item.updated_at)}
                    </strong>
                  </div>

                  <h4 style={{ margin: 0, color: "#f8fafc", fontSize: 18, lineHeight: 1.25 }}>
                    {item.title || "Dismissed recommendation"}
                  </h4>

                  {item.summary || item.reasoning_summary || item.detail ? (
                    <p style={bodyTextStyle}>{item.summary || item.reasoning_summary || item.detail}</p>
                  ) : null}

                  <div style={detailGridStyle}>
                    <div><strong style={{ color: "#f8fafc" }}>Framework:</strong> {item.affected_framework || "—"}</div>
                    <div><strong style={{ color: "#f8fafc" }}>Controls:</strong> {formatList(item.affected_controls)}</div>
                    <div><strong style={{ color: "#f8fafc" }}>Findings:</strong> {formatList(item.affected_findings)}</div>
                    <div><strong style={{ color: "#f8fafc" }}>Resources:</strong> {formatList(item.affected_resources)}</div>
                  </div>

                  <ModalActionRow
                    actions={[
                      {
                        key: `restore-${item.id}`,
                        label: isRestoring ? "Restoring…" : "Restore",
                        className: "btn btn-primary btn-sm",
                        disabled: isRestoring,
                        onClick: () => onRestore?.(item),
                      },
                    ]}
                  />
                </article>
              );
            })}
          </div>
        )}
      </ModalSection>

      <ModalActionRow
        actions={[
          {
            key: "close",
            label: "Close",
            className: "btn btn-primary btn-sm",
            onClick: onClose,
          },
        ]}
      />
    </ModalShell>
  );
}
